import { useEffect, useMemo, useState } from 'react';
import { LocationService } from '../services/location-service';
import { City, Province } from '../services/dto/response/location-response-dto';
import { locationCacheService } from '../services/location-cache-service';
import { useToast } from '../context/toast-context';
import { LocationSelector } from './LocationSelector';
import { LocationSkeleton } from './skeletons/location-skeleton';
import { useTranslation } from 'react-i18next';

interface LocationSelectorsProps {
  disabled?: boolean;
  onProvinceChange?: (provinceId: string) => void;
  onCityChange?: (cityId: string) => void;
  initialDepartmentId?: string;
  initialCityId?: string;
}

export function LocationSelectors({ 
  disabled = false,
  onProvinceChange,
  onCityChange,
  initialDepartmentId,
  initialCityId
}: LocationSelectorsProps) {
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [selectedProvince, setSelectedProvince] = useState(initialDepartmentId || "");
  const [selectedCity, setSelectedCity] = useState(initialCityId || "");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  
  const { showToast } = useToast();
  const { t } = useTranslation();
  const locationService = useMemo(() => new LocationService(), []);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const fetchProvinces = async () => {
      // Cargar caché desde localStorage al inicializar
      locationCacheService.loadFromStorage();
      
      // Verificar si ya tenemos provincias en caché
      const cachedProvinces = locationCacheService.getProvinces();
      if (cachedProvinces) {
        setProvinces(cachedProvinces);
        return; // No necesitamos hacer loading ni llamada a API
      }

      setIsLoading(true);

      try {
        await locationService.getProvinces(
          undefined,
          (data) => {
            if (!signal.aborted) {
              setProvinces(data);
            }
          },
          (errorMessage) => {
            if (!signal.aborted) {
              showToast(errorMessage, "error");
            }
          }
        );
      } finally {
        if (!signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchProvinces();

    return () => {
      controller.abort();
    };
  }, [locationService, showToast]);

  useEffect(() => {
    if (initialDepartmentId !== undefined) {
      setSelectedProvince(initialDepartmentId);
    }
    if (initialCityId !== undefined) {
      setSelectedCity(initialCityId);
    }
  }, [initialDepartmentId, initialCityId]);

  useEffect(() => {
    if (selectedProvince && Number(selectedProvince) > 0) {
      locationService.getCitiesByProvince(
        Number(selectedProvince),
        (data) => {
          setCities(data);
          setError("");
          
          if (selectedProvince === initialDepartmentId && initialCityId) {
            setSelectedCity(initialCityId);
          } else {
            setSelectedCity("");
          }
        },
        (error) => {
          setError(error);
          setCities([]);
          setSelectedCity("");
        }
      );
    } else {
      setCities([]);
      setSelectedCity("");
    }
  }, [locationService, selectedProvince, initialDepartmentId, initialCityId]);

  const handleProvinceChange = (value: string) => {
    setSelectedProvince(value);
    setSelectedCity("");
    setCities([]);
    onProvinceChange?.(value);
  };

  const handleCityChange = (value: string) => {
    setSelectedCity(value);
    onCityChange?.(value);
  };

  return (
    <div className="grid grid-cols-2 gap-4">
      {isLoading ? (
        <LocationSkeleton label={t("province")} />
      ) : (
        <LocationSelector
          label={t("province")}
          value={selectedProvince}
          onChange={handleProvinceChange}
          options={provinces.map((province) => ({
            id: province.id.toString(),
            name: province.name,
          }))}
          placeholder={t("select-province")}
          disabled={disabled || isLoading}
          error={error}
        />
      )}

      {isLoading || (selectedProvince && cities.length === 0) ? (
        <LocationSkeleton label={t("city")} />
      ) : (
        <LocationSelector
          label={t("city")}
          value={selectedCity}
          onChange={handleCityChange}
          options={cities.map((city) => ({
            id: city.id.toString(),
            name: city.name,
          }))}
          placeholder={t("select-city")}
          disabled={disabled || !selectedProvince || isLoading}
          error={error}
        />
      )}
    </div>
  );
}
